import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { RootState } from "../../../../../store/store";
import {
  GetInsuranceDocumentContent,
  GetPolicyActivities,
} from "../../../../../store/user/policyDetails/policyDetailsActions";
import { Timeline, timelineOppositeContentClasses } from "@mui/lab";
import CustomTimeline from "../../../../../common/CustomTimeline/CustomTimeline";
import { dateFormat } from "../../../../../constants/Constants";
import moment from "moment";
import ActivityCard from "./ActivityCard/ActivityCard";
import { Box, Typography, useMediaQuery } from "@mui/material";
import {
  IDocument,
  IDocumentContentRequest,
} from "../../../../../models/user/policyDetails/Document";
import { getBufferData } from "../../../../../utils/Utility";
import ActivityFilter from "../../../../../common/ActivityFilter/ActivityFilter";
import { useTranslation } from "react-i18next";
import CustomSkeleton from "../../../../../common/CustomSkeleton/CustomSkeleton";
import { ActivityStyles } from "../ActivityStyles";

const Policy = () => {
  const { policyId, carrier, system, lobCode } = useParams();
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const { policyDetailsHeader, policyTransactions, insuranceDocumentContent } =
    useAppSelector((state: RootState) => state.policyDetails);
  const { isSuccess, calledReducerType, isError, isLoading } = useAppSelector(
    (state: RootState) => state.common
  );

  const isMobile = useMediaQuery("(max-width:1024px)");

  const [actionType, setActionType] = useState<string | null>(null);
  const [currentClickFile, setCurrentClickFile] = useState<IDocument | null>(
    null
  );

  const handleFileClick = useCallback(
    (type: string, document: IDocument | null) => {
      if (!policyId || !document || !carrier || !system) return;
      setActionType(type);
      setCurrentClickFile(document);
      const requestParams: IDocumentContentRequest = {
        policyId,
        documentId: document.documentId,
        carrier,
        system,
      };
      dispatch(GetInsuranceDocumentContent(requestParams));
    },
    [dispatch, policyId, carrier, system]
  );

  useEffect(() => {
    if (
      policyId &&
      policyDetailsHeader?.accountId &&
      lobCode &&
      carrier &&
      system
    ) {
      dispatch(
        GetPolicyActivities({
          policyId,
          lobCode,
          carrier,
          system,
        })
      );
    }
  }, [
    policyId,
    policyDetailsHeader?.accountId,
    carrier,
    system,
    lobCode,
    dispatch,
  ]);

  useEffect(() => {
    if (
      isSuccess &&
      calledReducerType === "GetPolicyDocumentContent" &&
      insuranceDocumentContent &&
      actionType
    ) {
      const bufferArray = getBufferData(insuranceDocumentContent);
      const url = URL.createObjectURL(
        new Blob([bufferArray], { type: "application/pdf" })
      );
      if (actionType === "view") {
        window.open(url, "_blank");
      } else {
        const downloadLink = document.createElement("a");
        downloadLink.download = `New Business Package-Apr 20, 2024.pdf`;
        downloadLink.href = url;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    }
  }, [
    isSuccess,
    calledReducerType,
    insuranceDocumentContent,
    actionType,
    currentClickFile,
  ]);

  useEffect(() => {
    if (
      isError &&
      calledReducerType === "GetPolicyDocumentContent" &&
      actionType
    ) {
      alert(`Error in ${actionType} document`);
    }
  }, [isError, calledReducerType, actionType]);

  const [selectedPolicyTerm, setSelectedPolicyTerm] = useState<string | null>(
    null
  );

  const uniquePolicyTerms = Array.from(
    new Set(policyTransactions.map((transaction) => transaction.policyTerm))
  );

  const filteredTransactions = selectedPolicyTerm
    ? policyTransactions.filter(
        (transaction) => transaction.policyTerm === selectedPolicyTerm
      )
    : policyTransactions;

  return (
    <Box 
      id="policy_activity-container"
      data-test-id="test-policy_activity-container"
      aria-label="Policy Activity Container"
    >
      <ActivityFilter
        items={uniquePolicyTerms}
        onFilterChange={setSelectedPolicyTerm}
        label={t("Policy Term")}
      />
      {isLoading &&
      calledReducerType === "policyDetails/GetGetPolicyActivities" ? (
        <CustomSkeleton count={3} direction="column" height="120px" />
      ) : filteredTransactions.length > 0 ? (
        <Timeline
          id="policy_activity-timeline"
          data-test-id="test-policy_activity-timeline"
          aria-label="Policy Activity Timeline"
          sx={{
            padding: "0px",
            [`& .${timelineOppositeContentClasses.root}`]: { flex: 0.1 },
          }}
        >
          {[...filteredTransactions]
            .sort((a, b) => {
              const dateA = new Date(a.transactionDate).getTime();
              const dateB = new Date(b.transactionDate).getTime();
              if (dateA !== dateB) {
                return dateB - dateA;
              }
              const timeA = new Date(
                //Prepare TimeStamp
                `${a.transactionDate}T${a.transactionTime.split(" ")[0]}Z`
              ).getTime();
              const timeB = new Date(
                `${b.transactionDate}T${b.transactionTime.split(" ")[0]}Z`
              ).getTime();

              return timeB - timeA;

              //   if (dateA.getTime() !== dateB.getTime()) {
              //     return dateB.getTime() - dateA.getTime();
              //   }

              //   const parseTime = (timeString: string) => {
              //     if (!timeString) return 0;

              //     const timeWithoutZone = timeString.split(" ")[0];

              //     const [hours, minutes, seconds] = timeWithoutZone
              //       .split(":")
              //       .map(Number);

              //     return hours * 3600000 + minutes * 60000 + seconds * 1000;
              //   };

              //   const timeA = parseTime(a.transactionTime);
              //   const timeB = parseTime(b.transactionTime);

              //   return timeB - timeA;
            })
            .map((transaction, index) => (
              <CustomTimeline
                key={`policy-activity-${index}`}
                oppositeContent={moment(transaction.transactionDate).format(
                  dateFormat
                )}
                oppositeContentStyle={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                dotStyle={{
                  display: "flex",
                  alignItems: "center",
                }}
                children={
                  <ActivityCard
                    transaction={transaction}
                    isLast={
                      index ===
                      (policyTransactions
                        ? isMobile
                          ? policyTransactions.length - 2
                          : policyTransactions.length - 1
                        : false)
                    }
                    handleFileClick={handleFileClick}
                  />
                }
              />
            ))}
        </Timeline>
      ) : (
        <Box 
          id="policy_activity-no-data"
          data-test-id="test-policy_activity-no-data"
          aria-label="No Policy Data Available"
          sx={ActivityStyles.noDataAvailable}
        >
          <Typography 
            id="policy_activity-no-data-text"
            data-test-id="test-policy_activity-no-data-text"
            aria-label="No Policy Data Text"
            variant="h6" 
            fontSize="1rem"
          >
            {t("No Policies Available")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Policy;
